/** Title-case a person's display name for consistent presentation ("abed rahman" → "Abed Rahman"). */
export function titleCaseName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Split a brand name around the shared "alere" core. This is the single source of truth for
 * brand-name segmentation, shared by BrandWordmark (styled affixes) and brandDisplayName
 * (plain text), so the two renderings never drift. A brand whose name doesn't contain the
 * core returns the whole name as `core` with empty affixes.
 */
export function splitBrandName(name: string): { prefix: string; core: string; suffix: string } {
  const idx = name.toLowerCase().indexOf('alere');
  if (idx < 0) return { prefix: '', core: name, suffix: '' };
  return {
    prefix: name.slice(0, idx),
    core: name.slice(idx, idx + 5),
    suffix: name.slice(idx + 5),
  };
}

/**
 * Brand name for plain-text display, with the distinguishing affixes upper-cased to match the
 * BrandWordmark styling: "Aralere" → "ARalere", "AlereIT" → "AlereIT". A brand without the
 * "alere" core renders unchanged. DISPLAY ONLY — never persist this or send it to the API; the
 * stored business name is unchanged.
 */
export function brandDisplayName(name: string): string {
  const { prefix, core, suffix } = splitBrandName(name);
  return `${prefix.toUpperCase()}${core}${suffix.toUpperCase()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// CLIENT NAME IN THE ADMIN UI (04 #110; precedence RULED by the owner s115 after
// seeing both forms live in prod).
//
// THE RULE: the admin UI always shows the COMPANY NAME, which is
// `clients.displayName`. That is 02's own label for the field ("Company name" on a
// business client), and for a CONSUMER the person is the entity so displayName is
// their name. One rule, both client types, the SAME name on every admin screen.
//
// WARNING - THIS IS DELIBERATELY NOT THE RULE THE DOCUMENTS AND EMAILS USE, AND A
// LATER SESSION MUST NOT "RECONCILE" THE TWO:
//   admin UI (here)                 -> displayName ............... "Abed Business LLC2"
//   invoice / CRN / RFD PDF, emails -> legalName || displayName ... "Test Business Inc"
// The two surfaces have different jobs. The admin list is a WORKING surface the
// operator navigates by the name they know the client by; an invoice is a LEGAL
// artifact, and 02 locks legalName as "used on invoices" - the document, not the
// admin's list of documents. The accounting tools draw the same line. The
// consequence is ACCEPTED and recorded rather than discovered later as a bug: an
// admin card can read "Abed Business LLC2" while its own PDF's BILL TO reads
// "Test Business Inc". That is the split working, not drift.
//
// The server-side counterpart is api/src/lib/client-identity.ts#clientDisplayName
// (04 #106), which carries the DOCUMENT rule. The exports here are named
// differently on purpose so the two can never be mistaken for each other at an
// import site. `legalName` is deliberately NOT read here.
//
// WHY THE FEATURE EXISTS AT ALL (04 #110): an invoice always has TWO parties, and
// they differ whenever a linked person's bill is owed by their business -
//   invoices.clientId         = who it was ISSUED to = WHO INCURRED THE CHARGES
//   invoices.billedToClientId = the immutable billing authority stamped at ISSUE
//                             = WHO IS BILLED (02 RECEIVABLE ATTRIBUTION, s96)
// Every admin invoice card states both. Any NEW client-name rendering in the SPA
// imports from here rather than reaching for a field directly.
// ─────────────────────────────────────────────────────────────────────────────

/** The client field the admin name rule reads. A full `Client`, or any projection carrying it. */
export interface ClientNameSource {
  displayName: string;
}

/** The name the ADMIN UI shows for a client: the company name (`clients.displayName`). */
export function clientAdminName(client: ClientNameSource): string {
  return client.displayName;
}

/**
 * The lookup-safe form, for the common `clientById.get(id)` shape. Returns the
 * client's admin name, or `fallback` (normally the raw id) when the record is not
 * loaded - never a blank, so a name can never silently vanish from a money surface.
 */
export function clientAdminLabel(client: ClientNameSource | null | undefined, fallback: string): string {
  return client ? clientAdminName(client) : fallback;
}
